import { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    Paper,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    CircularProgress,
    Tooltip
} from "@mui/material";
import {
    Description as FormsIcon,
    Add as AddIcon,
    Visibility as ViewIcon,
    Edit as EditIcon,
    AutoAwesome as AIIcon,
    Delete as DeleteIcon
} from "@mui/icons-material";
import { useMessageContext } from "../../contexts/MessageContext";
import { useCompany } from "../../contexts/CompanyContext";
import { getErrorMessage } from "../../enums/ErrorMessages";
import { getTraineeForms, deleteTraineeForm, getFormById, generateAISummary, assignTemplateToTrainee, getFormTemplates } from "../../services/FormTemplateService";
import SendFormToClientDialog from "./SendFormToClientDialog";
import FormViewDialog from "./FormViewDialog";
import FormEditDialog from "./FormEditDialog";
import AISummaryDialog from "./AISummaryDialog";
import DeleteFormDialog from "../forms/DeleteFormDialog";
import {colors, borders, shadows} from "../ui/colors";
import {t as coachT} from "../dashboard/coach/coachTokens";
import {StatusChip} from "../ui";
import {RTL} from "../../cache/CacheRtl";

const formatDate = (timestamp) => {
    if (!timestamp) return null;
    const date = new Date(timestamp);
    return date.toLocaleDateString('he-IL');
};

const mapStatus = (serverStatus) => {
    const statusMap = {
        'PENDING': 'pending',
        'PARTIAL_ANSWERED': 'pending',
        'COMPLETED': 'completed'
    };
    return statusMap[serverStatus] || 'pending';
};

const tdBase = {borderBottom: borders.default, py: 0, px: 2, height: "56px", fontSize: "13px", color: colors.textPrimary};
const thBase = {...tdBase, height: "42px", color: colors.textMuted, fontSize: "11px", fontWeight: 600, backgroundColor: colors.bg, letterSpacing: "0.3px"};

const tdCoach = {borderBottom: `1px solid ${coachT.borderLight}`, py: 0, px: 2, height: "56px", fontSize: "13px", color: coachT.text};
const thCoach = {...tdCoach, height: "42px", color: coachT.t3, fontSize: "10.5px", fontWeight: 700, backgroundColor: coachT.bg, textTransform: "uppercase", letterSpacing: ".06em", borderBottom: `1px solid ${coachT.border}`};

const ClientPageForms = ({ client, coach = false }) => {
    const th = coach ? thCoach : thBase;
    const td = coach ? tdCoach : tdBase;
    const { notificationHandler } = useMessageContext();
    const { company } = useCompany();

    const isFormsEnabled = true;

    const [forms, setForms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reload, setReload] = useState(false);
    const [availableTemplates, setAvailableTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);

    const [sendDialogOpen, setSendDialogOpen] = useState(false);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [aiDialogOpen, setAIDialogOpen] = useState(false);
    const [aiSummaryLoading, setAISummaryLoading] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedForm, setSelectedForm] = useState(null);

    const fetchForms = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getTraineeForms(client.id);
            const data = response.data;

            const mappedForms = (data.content || []).map(form => ({
                id: form.id,
                title: form.name || form.formTemplateName,
                type: form.formTemplateName || 'שאלון',
                sentDate: formatDate(form.createdDate ? form.createdDate * 1000 : null),
                completedDate: formatDate(form.completedDate ? form.completedDate * 1000 : null),
                status: mapStatus(form.status),
                description: form.description,
                logoUrl: form.logoUrl,
                formTemplateId: form.formTemplateId,
                traineeId: form.traineeId,
                aiKeyPoints: form.aiKeyPoints || [],
                aiRecommendations: form.aiRecommendations || [],
                aiSummaryGeneratedAt: form.aiSummaryGeneratedAt
            }));

            setForms(mappedForms);
        } catch (e) {
            notificationHandler.error(getErrorMessage(e.response?.data?.errorCode) || "שגיאה בטעינת הטפסים");
            setForms([]);
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client.id]);

    useEffect(() => {
        fetchForms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client.id, reload]);

    const handleReload = () => {
        setReload(prev => !prev);
    };

    const handleOpenSendDialog = async () => {
        setSendDialogOpen(true);
        setTemplatesLoading(true);
        try {
            const response = await getFormTemplates(0, 100, company?.id);
            const templates = response.data?.content || [];
            const mappedTemplates = templates.map(t => ({
                id: t.id,
                formTemplateId: t.id,
                title: t.name,
                description: t.description
            }));
            setAvailableTemplates(mappedTemplates);
        } catch (e) {
            console.error('Error fetching form templates:', e);
            setAvailableTemplates([]);
        } finally {
            setTemplatesLoading(false);
        }
    };

    const handleViewForm = (form) => {
        setSelectedForm(form);
        setViewDialogOpen(true);
    };

    const handleEditForm = (form) => {
        setSelectedForm(form);
        setEditDialogOpen(true);
    };

    const handleAISummary = async (form) => {
        setSelectedForm(form);
        setAIDialogOpen(true);
        setAISummaryLoading(true);

        try {
            let response = await getFormById(form.id);
            let freshForm = response.data;

            if (freshForm.status === 'COMPLETED' &&
                (!freshForm.aiKeyPoints || freshForm.aiKeyPoints.length === 0)) {
                try {
                    response = await generateAISummary(form.id);
                    freshForm = response.data;
                } catch (genError) {
                    console.error("Failed to generate AI summary:", genError);
                }
            }

            setSelectedForm({
                ...form,
                aiKeyPoints: freshForm.aiKeyPoints || [],
                aiRecommendations: freshForm.aiRecommendations || [],
                aiSummaryGeneratedAt: freshForm.aiSummaryGeneratedAt
            });
        } catch (e) {
            console.error("Failed to fetch form:", e);
        } finally {
            setAISummaryLoading(false);
        }
    };

    const handleDeleteClick = (form) => {
        setSelectedForm(form);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (selectedForm) {
            try {
                await deleteTraineeForm(selectedForm.id);
                notificationHandler.success("הטופס נמחק בהצלחה");
                handleReload();
            } catch (e) {
                notificationHandler.error(getErrorMessage(e.response?.data?.errorCode) || "שגיאה במחיקת הטופס");
            }
        }
        setDeleteDialogOpen(false);
        setSelectedForm(null);
    };

    const handleSendForm = async (template, isMandatory, formName) => {
        if (!template || !client?.id) return;

        try {
            const templateId = template.formTemplateId || template.id;

            if (!templateId || templateId === 0) {
                console.error('No valid template ID found:', template);
                notificationHandler.error("שגיאה: לא נמצא מזהה תבנית תקין");
                return;
            }

            await assignTemplateToTrainee(templateId, client.id, formName, isMandatory);
            notificationHandler.success("הטופס נשלח בהצלחה");
            handleReload();
        } catch (e) {
            console.error('[ClientPageForms] handleSendForm error:', e);
            if (e.response?.status === 409) {
                notificationHandler.info("הטופס כבר נשלח למתאמן זה");
            } else {
                notificationHandler.error(getErrorMessage(e.response?.data?.errorCode) || "שגיאה בשליחת הטופס");
            }
        }
    };

    const getStatusChipForForm = (status) => {
        const statusConfig = {
            completed: { variant: "success", label: "הושלם" },
            pending: { variant: "warning", label: "ממתין למילוי" },
            draft: { variant: "default", label: "טיוטה" }
        };
        const config = statusConfig[status] || statusConfig.pending;
        return <StatusChip variant={config.variant} label={config.label} />;
    };

    return (
        <>
            <Box sx={coach ? {} : { margin: "20px" }}>
                <RTL>
                <Paper
                    dir="rtl"
                    sx={{
                        backgroundColor: coach ? coachT.white : colors.white,
                        border: coach ? `1px solid ${coachT.border}` : borders.default,
                        borderRadius: coach ? coachT.r : borders.radius.lg,
                        boxShadow: coach ? coachT.sh : shadows.card,
                        overflow: "hidden",
                    }}
                    elevation={0}
                >
                    {/* Header */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: coach ? "16px 22px" : "16px 20px",
                            backgroundColor: coach ? coachT.bg : colors.bg,
                            borderBottom: coach ? `1px solid ${coachT.border}` : borders.default
                        }}
                    >
                        <Box sx={{display: "flex", alignItems: "center", gap: coach ? 1.5 : 1}}>
                            {coach ? (
                                <Box sx={{width: 40, height: 40, borderRadius: "11px", background: coachT.white, border: `1px solid ${coachT.border}`, display: "flex", alignItems: "center", justifyContent: "center"}}>
                                    <FormsIcon sx={{color: coachT.t2, fontSize: 22}}/>
                                </Box>
                            ) : (
                                <FormsIcon sx={{ color: colors.textSecondary, fontSize: "20px" }} />
                            )}
                            <Box>
                                <Typography
                                    sx={{
                                        fontWeight: coach ? 800 : 600,
                                        fontSize: coach ? "17px" : "16px",
                                        color: coach ? coachT.text : colors.textPrimary,
                                        letterSpacing: coach ? "-0.2px" : 0,
                                    }}
                                >
                                    טפסים{coach ? "" : ` - ${client?.name}`}
                                </Typography>
                                {!coach && (
                                    <Typography sx={{fontSize: "14px", color: colors.textSecondary, marginTop: "4px"}}>
                                        כל הטפסים המשויכים למתאמן
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        {isFormsEnabled && (coach ? (
                            <Box component="button" onClick={handleOpenSendDialog} sx={{
                                display: "inline-flex", alignItems: "center", gap: "5px",
                                background: coachT.green, color: "#fff",
                                height: 34, px: "15px", borderRadius: coachT.rs,
                                fontSize: "13px", fontWeight: 600,
                                border: "none", cursor: "pointer", fontFamily: "inherit",
                                transition: "all .14s",
                                "&:hover": {background: coachT.greenDark, transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(1,187,167,.35)"},
                            }}>
                                <AddIcon sx={{fontSize: 16}}/>
                                שליחת טופס למתאמן
                            </Box>
                        ) : (
                            <Button
                                onClick={handleOpenSendDialog}
                                variant="contained"
                                startIcon={<AddIcon />}
                                sx={{
                                    backgroundColor: colors.primary,
                                    color: colors.white,
                                    fontSize: "13px",
                                    padding: "8px 16px",
                                    "&:hover": {
                                        backgroundColor: colors.primaryDark
                                    },
                                    "&:disabled": {
                                        backgroundColor: colors.border,
                                        color: colors.textMuted
                                    }
                                }}
                                size="small"
                            >
                                שליחת טופס למתאמן
                            </Button>
                        ))}
                    </Box>

                    {/* Content */}
                    <Box sx={{ padding: coach ? "16px 22px" : "20px" }}>
                        {!isFormsEnabled ? (
                            <Box sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: "200px",
                                flexDirection: "column",
                                gap: 2
                            }}>
                                <FormsIcon sx={{ fontSize: "48px", color: colors.borderHover }} />
                                <Typography sx={{
                                    fontSize: "16px",
                                    fontWeight: 500,
                                    color: colors.textSecondary,
                                    textAlign: "center"
                                }}>
                                    פיצ׳ר זה בפיתוח
                                </Typography>
                                <Typography sx={{
                                    fontSize: "14px",
                                    color: colors.textMuted,
                                    textAlign: "center"
                                }}>
                                    ניהול טפסים יהיה זמין בקרוב
                                </Typography>
                            </Box>
                        ) : loading ? (
                            <Box sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                minHeight: "200px"
                            }}>
                                <CircularProgress sx={{ color: coach ? coachT.green : colors.primary }} />
                            </Box>
                        ) : forms.length > 0 ? (
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={th}>שם הטופס</TableCell>
                                            <TableCell sx={th}>סוג</TableCell>
                                            <TableCell sx={th}>תאריך שליחה</TableCell>
                                            <TableCell sx={th}>סטטוס</TableCell>
                                            <TableCell sx={th}>תאריך מילוי השאלון</TableCell>
                                            <TableCell sx={th} style={{textAlign: "left"}}>פעולות</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {forms.map((form) => (
                                            <TableRow
                                                key={form.id}
                                                sx={{
                                                    "&:hover": { backgroundColor: coach ? coachT.greenLight : colors.bg },
                                                    transition: "background-color 0.2s"
                                                }}
                                            >
                                                <TableCell sx={td}>
                                                    {coach ? (
                                                        <Box>
                                                            <Typography sx={{fontSize: "14px", fontWeight: 600, color: coachT.text}}>
                                                                {form.title}
                                                            </Typography>
                                                            {form.isMandatory && (
                                                                <StatusChip variant="error" label="חובה למילוי" />
                                                            )}
                                                        </Box>
                                                    ) : (
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                                            <Box
                                                                sx={{
                                                                    width: 40,
                                                                    height: 40,
                                                                    borderRadius: borders.radius.md,
                                                                    backgroundColor: colors.primaryLight,
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center"
                                                                }}
                                                            >
                                                                <FormsIcon sx={{ color: colors.primary, fontSize: "20px" }} />
                                                            </Box>
                                                            <Box>
                                                                <Typography sx={{
                                                                    fontSize: "14px",
                                                                    fontWeight: 600,
                                                                    color: colors.textPrimary
                                                                }}>
                                                                    {form.title}
                                                                </Typography>
                                                                {form.isMandatory && (
                                                                    <StatusChip variant="error" label="חובה למילוי" />
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{...td, color: coach ? coachT.t2 : colors.textSecondary}}>
                                                    {form.type}
                                                </TableCell>
                                                <TableCell sx={{...td, color: coach ? coachT.t2 : colors.textSecondary}}>
                                                    {form.sentDate}
                                                </TableCell>
                                                <TableCell sx={td}>
                                                    {getStatusChipForForm(form.status)}
                                                </TableCell>
                                                <TableCell sx={{...td, color: coach ? coachT.t2 : colors.textSecondary}}>
                                                    {form.completedDate || "טרם מולא"}
                                                </TableCell>
                                                <TableCell sx={td}>
                                                    <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                                                        <Tooltip title="צפייה">
                                                            <IconButton
                                                                onClick={() => handleViewForm(form)}
                                                                size="small"
                                                                sx={{
                                                                    color: coach ? coachT.t2 : colors.primary,
                                                                    "&:hover": {
                                                                        backgroundColor: coach ? coachT.greenLight : colors.primaryLight,
                                                                        color: coach ? coachT.green : undefined
                                                                    }
                                                                }}
                                                            >
                                                                <ViewIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        {form.status !== "completed" && (
                                                            <Tooltip title="עריכה ומילוי תשובות">
                                                                <IconButton
                                                                    onClick={() => handleEditForm(form)}
                                                                    size="small"
                                                                    sx={{
                                                                        color: colors.primary,
                                                                        "&:hover": {
                                                                            backgroundColor: colors.primaryLight
                                                                        }
                                                                    }}
                                                                >
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                        <Tooltip title="סיכום AI">
                                                            <IconButton
                                                                onClick={() => handleAISummary(form)}
                                                                size="small"
                                                                sx={{
                                                                    color: coach ? coachT.t2 : colors.primary,
                                                                    "&:hover": {
                                                                        backgroundColor: coach ? coachT.greenLight : colors.primaryLight,
                                                                        color: coach ? coachT.green : undefined
                                                                    }
                                                                }}
                                                            >
                                                                <AIIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="מחיקה">
                                                            <IconButton
                                                                onClick={() => handleDeleteClick(form)}
                                                                size="small"
                                                                sx={{
                                                                    color: coach ? coachT.t2 : colors.error,
                                                                    "&:hover": {
                                                                        backgroundColor: coach ? coachT.redLight : colors.errorBg,
                                                                        color: coach ? coachT.red : undefined
                                                                    }
                                                                }}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Box sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: "200px",
                                flexDirection: "column",
                                gap: 2
                            }}>
                                <FormsIcon sx={{ fontSize: "48px", color: coach ? coachT.border : colors.borderHover }} />
                                <Typography sx={{
                                    fontSize: "16px",
                                    fontWeight: 500,
                                    color: coach ? coachT.t3 : colors.textSecondary,
                                    textAlign: "center"
                                }}>
                                    אין עדיין טפסים משויכים למתאמן הזה
                                </Typography>
                                <Typography sx={{
                                    fontSize: "14px",
                                    color: coach ? coachT.t3 : colors.textMuted,
                                    textAlign: "center"
                                }}>
                                    לחץ על "שליחת טופס למתאמן" כדי לשלוח טופס ראשון
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Paper>
                </RTL>
            </Box>

            {/* Dialogs */}
            <SendFormToClientDialog
                open={sendDialogOpen}
                onClose={() => setSendDialogOpen(false)}
                onSend={handleSendForm}
                client={client}
                availableForms={availableTemplates}
                loading={templatesLoading}
                coach={coach}
            />

            <FormViewDialog
                open={viewDialogOpen}
                onClose={() => {
                    setViewDialogOpen(false);
                    setSelectedForm(null);
                }}
                form={selectedForm}
                clientName={client?.name}
                coach={coach}
            />

            <FormEditDialog
                open={editDialogOpen}
                onClose={() => {
                    setEditDialogOpen(false);
                    setSelectedForm(null);
                }}
                onSave={() => {
                    setEditDialogOpen(false);
                    setSelectedForm(null);
                    handleReload();
                }}
                form={selectedForm}
                clientName={client?.name}
                coach={coach}
            />

            <AISummaryDialog
                open={aiDialogOpen}
                onClose={() => {
                    setAIDialogOpen(false);
                    setSelectedForm(null);
                    setAISummaryLoading(false);
                }}
                form={selectedForm}
                clientName={client?.name}
                loading={aiSummaryLoading}
                coach={coach}
            />

            <DeleteFormDialog
                open={deleteDialogOpen}
                onClose={() => {
                    setDeleteDialogOpen(false);
                    setSelectedForm(null);
                }}
                onConfirm={handleDeleteConfirm}
                formTitle={selectedForm?.title}
                coach={coach}
            />
        </>
    );
};

export default ClientPageForms;